/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import uuid from 'uuid';
import { PDPAnalytics } from '../../../../analytics';
import { PDP_SECTION, PDP_ACTION } from '../../../../utils/pdpUtils/pdpEnums';
import TranslationUtil from '../../../../utils/translation';

const { getMessage } = TranslationUtil;

class RelatedApps extends Component {
    onAppClick(e, appObj) {
        const { PDPDataObject: { actionHandlerCallback } } = this.props;
        const { sectionId } = this.sectionData;
        const guid = uuid.v4();

        const actionParam = {
            actionObject: appObj,
            guid,
        };
        actionHandlerCallback(e, sectionId, appObj.actionId, actionParam);

        PDPAnalytics.relatedAppClick(appObj, guid, sectionId);
    }

    get sectionData() {
        const { PDPDataObject, appsList } = this.props;
        return this.validateAndProcessData(PDPDataObject, appsList);
    }

    validateAndProcessData({ contentfulData }, appsList) {
	    if (!contentfulData.relatedApps || !contentfulData.relatedApps.length) {
	        return null;
	    }
	    const { relatedApps } = contentfulData;
	    const appList = [];
	    relatedApps.forEach((sapcode) => {
	        const filteredApps = appsList.filter(app => (app.sapcode === sapcode));
	        if (filteredApps.length) {
	            appList.push({
	            app: filteredApps[0],
	            actionId: PDP_ACTION.APP_CLICK,
	        });
	        }
	    });
	    return { relatedAppList: appList, sectionId: PDP_SECTION.RELATED_APPS };
    }

    renderRelatedApps() {
        const { relatedAppList } = this.sectionData;
        const pdpAppTagline = (appSectionBasedFields) => {
            if (!appSectionBasedFields) {
                return '';
            }
            const pdpData = appSectionBasedFields.filter(field => field.appSectionFilter.includes('pdp'));
            return pdpData && pdpData.length > 0 ? pdpData[0].localizedTagline : '';
        };
        return relatedAppList.map((relatedApp) => {
            const {
                appFamilyName, iconURL, appSectionBasedFields, sapcode,
            } = relatedApp.app;
            const appDesc = pdpAppTagline(appSectionBasedFields);
            return (
                <div
                    className="related-app"
                    tabIndex="0"
                    role="link"
                    aria-label={`${appFamilyName}, ${appDesc}`}
                    key={`related-app-${sapcode}`}
                    onKeyDown={(e) => {
                        if (e.keyCode === 13) {
                            this.onAppClick(e, relatedApp);
                        }
                    }}
                    onClick={(e) => {
                        this.onAppClick(e, relatedApp);
                    }}>
                    <img src={iconURL} className="related-app-icon" alt="" /> {/* TODO: alt={`${appFamilyName} logo`} */}
                    <div className="related-app-desc">
                        <div className="related-app-link">
                            {appFamilyName}
                        </div>
                        <div className="related-app-detail" title={appDesc}>
                            {appDesc}
                        </div>
                    </div>
                </div>
            );
        });
    }

    render() {
        // Related Apps
        return this.sectionData && (
            <div className="related-apps right-panel-compt">
                <div className="related-apps-header">
                    {getMessage('ccx.pdp.related-apps.section-name')}
                </div>
                <div className="related-apps-list">
                    {this.renderRelatedApps()}
                </div>
            </div>
        );
    }
}

export default observer(RelatedApps);
